import { useMutation } from "@apollo/client";
import { parseError } from "Apollo";
import Button from "components/Button";
import { FileSelector } from "components/FileSelector";
import Page from "components/Page";
import useFileUploader from "hooks/useFileUploader";
import { useState } from "react";
import { BsTrash } from "react-icons/bs";
import { toast } from "react-toastify";
import BasicView from "./BasicView";
import CompanyView from "./CompanyView";
import { CERTIFICATES, COMPANY_TYPES, CURRENCY_TYPES } from "./const";
import { RegisterContext } from "./context";
import { SUBMIT_NEW_SUPPLIER_FORM, SUPPLIER_SAVE_DRAFT } from "./graphql";

const DraftView = ({ originalSupplier, originDraftData }) => {
  const [basicInfo, setBasicInfo] = useState(
    originDraftData.basicInfo || {
      companyNameCn: "",
      contactPerson: "",
      address: "",
      phone: "",
      numberOfEmployees: "",
      yearOfEstablished: "",
      majorProducts: "",
      majorMarket: "",
      equipmentAndFacilities: "",
      photos: [],
    },
  );
  const [companies, setCompanies] = useState(
    originDraftData.companies || [
      {
        businessType: "FOREIGN",
        invoiceType: "NORMAL",
        companyName: "",
        address: "",
        companyType: COMPANY_TYPES[0],
        businessLicense: [],
        acceptedCurrencies: CURRENCY_TYPES.reduce(
          (acc, cur) => ({ ...acc, [cur]: false }),
          {},
        ),
        payeeName: "",
        payeeBankAccountNumber: "",
        payeeBankBranchInfo: "",
        payeeBankAddress: "",
        swiftCode: "",
      },
    ],
  );

  const [certificates, setCertificates] = useState(
    originDraftData.certificates ||
      CERTIFICATES.reduce((acc, cur) => ({ ...acc, [cur]: null }), {}),
  );

  const [hasQcDepartment, setHasQcDepartment] = useState(
    originDraftData.hasQcDepartment || false,
  );
  const [inventoryManagement, setInventoryManagement] = useState(
    originDraftData.inventoryManagement || "",
  );
  const [informationTechnology, setInformationTechnology] = useState(
    originDraftData.informationTechnology || "",
  );

  const certificatesUploader = useFileUploader((urls, options) => {
    setCertificates((prev) => ({
      ...prev,
      [options.type]: urls[0],
    }));
  });

  const [saveDraft, saveDraftRes] = useMutation(SUPPLIER_SAVE_DRAFT);
  const [submit, submitRes] = useMutation(SUBMIT_NEW_SUPPLIER_FORM, {
    onCompleted() {
      toast.success("Registration form submitted successfully!");
    },
    onError(error) {
      toast.error(parseError(error));
    },
  });

  const disabled = originalSupplier.state !== "DRAFT";

  const draftData = {
    basicInfo,
    companies,
    certificates,
    hasQcDepartment,
    inventoryManagement,
    informationTechnology,
  };

  // console.log("draftData", draftData);

  function onChangeBasicInfo(values) {
    setBasicInfo((prev) => ({ ...prev, ...values }));
  }

  function onChangeCompany(index, values) {
    setCompanies((prev) =>
      prev.map((c, i) => (i === index ? { ...c, ...values } : c)),
    );
  }
  console.log("companies", companies);

  function addNewCompany() {
    setCompanies([
      ...companies,
      {
        businessType: "FOREIGN",
        companyType: COMPANY_TYPES[0],
        name: "",
        address: "",
        type: COMPANY_TYPES[0],
        businessLicense: [],
        acceptedCurrencies: [],
        invoiceType: "NORMAL",
        taxPercent: 0,
      },
    ]);
  }

  function validate() {
    // validate basic info, contact and phone must be filled

    if (!basicInfo.companyNameCn) {
      toast.error(
        "Company Name (公司名称) is required. 必填项，请填写公司名称。",
      );
      return false;
    }

    if (!basicInfo.contactPerson) {
      toast.error(
        "Contact Person (联系人) is required. 必填项，请填写联系人。",
      );
      return false;
    }
    if (!basicInfo.phone) {
      toast.error(
        "Phone Number (电话号码) is required. 必填项，请填写电话号码。",
      );
      return false;
    }

    for (let i = 0; i < companies.length; i++) {
      const company = companies[i];
      if (!company.name) {
        toast.error(
          `Company #${i + 1} Name (公司名称) is required. 必填项，请填写公司名称。`,
        );
        return false;
      }
      if (!company.address) {
        toast.error(
          `Company #${i + 1} Address (公司地址) is required. 必填项，请填写公司地址。`,
        );
        return false;
      }
      if (
        !Object.values(company.acceptedCurrencies || {}).some((v) => v === true)
      ) {
        toast.error(
          `Company #${i + 1} must select at least one accepted currency (接受币种). 至少选择一个接受币种。`,
        );
        return false;
      }
      if (company.businessType === "DOMESTIC" && !company.invoiceType) {
        toast.error(
          `Company #${i + 1} Invoice Type (发票类型) is required. 必填项，请选择发票类型。`,
        );
        return false;
      }

      if (!company.businessLicense || company.businessLicense.length === 0) {
        toast.error(
          `Company #${i + 1} Please upload Business License (请上传营业执照)。`,
        );
        return false;
      }

      // payee Information
      if (
        !company.payeeName ||
        !company.payeeBankAccountNumber ||
        !company.payeeBankBranchInfo ||
        !company.payeeBankAddress
      ) {
        toast.error(
          `Company #${i + 1} Please complete the Payee Information (请填写收款人信息)。`,
        );
        return false;
      }

      // If for foreign company, swift code is also required
      if (company.businessType === "FOREIGN" && !company.swiftCode) {
        toast.error(
          `Company #${i + 1} Please provide the SWIFT Code for the Payee (请提供收款人的SWIFT代码)。`,
        );
        return false;
      }
    }

    return true;
  }

  function saveHandler() {
    saveDraft({
      variables: {
        draftData: JSON.stringify(draftData),
      },
      onCompleted(res) {
        toast.success("Saved 已保存.");
      },
    });
  }

  function submitHandler() {
    if (!validate()) return;

    if (
      window.confirm(
        "确认提交表单吗？提交后将无法再编辑。 Are you sure you want to submit the form? Once you submit, you cannot edit it anymore. ",
      )
    ) {
      submit({
        variables: { draftData: JSON.stringify(draftData) },
      });
    }
  }

  return (
    <RegisterContext.Provider value={{ companies, setCompanies, disabled }}>
      <Page
        title="Join Waboba Supply Chain"
        topbarRightView={
          <div className="flex items-center space-x-6">
            <Button
              onClick={saveHandler}
              loading={saveDraftRes.loading}
              disabled={disabled}
            >
              Save 保存
            </Button>
            {originalSupplier.state === "DRAFT" && (
              <Button
                primary
                onClick={submitHandler}
                loading={submitRes.loading}
              >
                Submit for Review 提交审核
              </Button>
            )}
          </div>
        }
      >
        <div className="p-10 text-sm">
          <div className="mt-4 px-[1px]">
            <p>Hello, {originalSupplier.nickName}, 你好! </p>
            <p>
              欢迎加入Waboba大家庭！我们很高兴能与您合作，并期待着成功的合作伙伴关系。
              通过加入我们的供应链，您将获得独特的机会和资源，帮助您的业务蓬勃发展。
              请填写下面的注册表格以开始。我们的团队将审核您的申请，并尽快与您联系。
              Welcome to the Waboba family! We are excited to collaborate with
              you and look forward to a successful partnership. By joining our
              supply chain, you'll gain access to unique opportunities and
              resources to help your business thrive. Please fill out the
              registration form below to get started. Our team will review your
              application and reach out to you shortly.
            </p>
            <p>
              我们承诺会对贵公司在此表单中提交的所有信息严格保密，绝不会向任何第三方泄露。您的资料仅用于Waboba供应链资质审核及后续合作相关事宜。请您放心填写。
              We assure you that all the information your company submits in
              this form will be kept strictly confidential and will not be
              disclosed to any third party. Your data will only be used for
              Waboba's supply chain qualification review and related cooperation
              matters. Please fill out the form with confidence.
            </p>
          </div>

          <div className="mt-2">
            {(originalSupplier.state === "PENDING" ||
              originalSupplier.state === "VERIFIED") && (
              <div className="text-green-600 font-semibold text-base">
                ✅ 我们已经收到您的申请, 请耐心等待审核结果。 We have received
                your application, please wait patiently for the review result.
              </div>
            )}

            {originalSupplier.state === "DRAFT" && (
              <div className="opacity-70">
                您可以随时通过点击“保存”按钮来保存您的进度。当您准备好提交时，请点击“提交审核”按钮。
                You can save your progress at any time by clicking the "Save"
                button. When you are ready to submit your application, click the
                "Submit for Review" button.
              </div>
            )}
          </div>

          <div className="mt-6 space-y-8">
            <BasicView
              disabled={disabled}
              value={basicInfo}
              onChange={onChangeBasicInfo}
            />

            <div className="card">
              <h4>企业信息</h4>
              <div>
                {companies.map((company, index) => (
                  <CompanyView
                    key={index}
                    index={index}
                    disabled={disabled}
                    company={company}
                    onChange={(values) => onChangeCompany(index, values)}
                    deleteCompany={() =>
                      setCompanies((prev) => prev.filter((_, i) => i !== index))
                    }
                  />
                ))}
              </div>

              {!disabled && (
                <div className="mt-4">
                  <Button onClick={addNewCompany}>
                    + Add Company 添加新公司
                  </Button>
                  <div className="opacity-70 text-xs mt-1">
                    如果贵公司有多个分支机构或子公司，请为每个实体分别添加一条记录。
                    <br />
                    If your company has multiple branches or subsidiaries,
                    please add a separate entry for each entity.
                  </div>
                </div>
              )}
            </div>

            <div className="card">
              <h4>Certificates 证书</h4>
              <div className="mt-2 -mx-2">
                <table>
                  <tbody>
                    {CERTIFICATES.map((cert) => (
                      <tr
                        key={cert}
                        className="border-y border-gray-100 dark:border-gray-700"
                      >
                        <td className="align-baseline">
                          <label>{cert}:</label>
                        </td>
                        <td className="w-full align-baseline">
                          {certificates[cert] ? (
                            <div className="flex items-center space-x-2">
                              <a
                                href={certificates[cert]}
                                target="_blank"
                                rel="noreferrer"
                                className="underline text-sm"
                              >
                                {certificates[cert]}
                              </a>
                              <Button
                                disabled={disabled}
                                danger
                                onClick={() => {
                                  setCertificates((prev) => ({
                                    ...prev,
                                    [cert]: null,
                                  }));
                                }}
                              >
                                <BsTrash />
                              </Button>
                            </div>
                          ) : null}
                          <FileSelector
                            disabled={disabled}
                            multiple={false}
                            title="Upload 上传"
                            accept="image/*,application/pdf"
                            onChange={(e) => {
                              const files = [...e.target.files];
                              certificatesUploader.startUpload(files, {
                                type: cert,
                              });
                              e.target.value = null;
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </Page>
    </RegisterContext.Provider>
  );
};

export default DraftView;
